import React, { useState, useEffect, useContext } from 'react'
import createAuth0Client, {
    Auth0ClientOptions,
    getIdTokenClaimsOptions, GetTokenSilentlyOptions, GetTokenWithPopupOptions,
    IdToken, LogoutOptions,
    PopupLoginOptions, RedirectLoginOptions,
    RedirectLoginResult
} from '@auth0/auth0-spa-js'
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client'
import {UserProductInterface} from './interfaces/user.product.interface';
import QueryString from "query-string";
import {getAuthSettings, getUserLogin} from "./api/Auth0API";
import {config} from "./config";
import {
    hasSuiteManagerSession,
    redirectToSuiteManagerLogout,
} from './utils/suiteManagerAuth';
import {
    clearStoredAuthLoginErrorMessage,
    hasStoredAuthLoginErrorMessage,
    isAuthLoginErrorRoute,
    extractAuthLoginErrorMessage,
    setAuthLoginErrorState,
} from "./utils/authLoginError";

export interface Auth0RedirectState {
    targetUrl?: string
}

export interface Auth0User  {
    sub: string,
    name: string,
    email: string
}

interface Auth0Context {
    user?: Auth0User
    isAuthenticated: boolean
    loading: boolean
    auth0Disabled: boolean
    isPopupOpen: boolean
    userProduct?: UserProductInterface,
    loginWithPopup(o?: PopupLoginOptions): Promise<void>
    handleRedirectCallback(): Promise<RedirectLoginResult>
    getIdTokenClaims(o?: getIdTokenClaimsOptions): Promise<IdToken>
    loginWithRedirect(o?: RedirectLoginOptions): Promise<void>
    getTokenSilently(o?: GetTokenSilentlyOptions): Promise<string | undefined>
    getTokenWithPopup(o?: GetTokenWithPopupOptions): Promise<string | undefined>
    logout(o?: LogoutOptions): void
}
interface Auth0ProviderOptions {
    children: React.ReactElement
    onRedirectCallback(result: RedirectLoginResult): void
}

let _initOptions: Auth0ClientOptions, _client: Auth0Client
const getAuth0Client = ():Promise<Auth0Client> => {
    return new Promise<Auth0Client>(async (res, rej) => {
        try {
            _client = await createAuth0Client(_initOptions)
            res(_client)
        } catch (e) {
            rej(new Error('getAuth0Client Error'))
        }
    })
}
// getToken outside hook
export const getTokenSilently = async (options?: GetTokenSilentlyOptions) => {
    if(!_client) {
        _client = await getAuth0Client()
    }
    return await _client.getTokenSilently(options)
}
// destroy session outside hook
export const logoutGlobal = (options?: LogoutOptions) => {
    if (hasSuiteManagerSession()) {
        redirectToSuiteManagerLogout(options?.returnTo);
        return;
    }
    if(!!_client){
        _client!.logout(options);
    }
}

export const Auth0Context = React.createContext<Auth0Context | null>(null)
export const useAuth0 = () => useContext(Auth0Context)!
const encodeSuiteManagerData = (payload: object) => {
    return window.btoa(JSON.stringify(payload))
}
const decodeSuiteManagerData = (encodedData: string) => {
    return JSON.parse(window.atob(encodedData))
}
export const redirectToSuiteManagerLogin = (email?: string) => {
    if (!!config.appLinks.suiteManager) {
        const suiteManagerUrl = config.appLinks.suiteManager || '/'
        console.log(`redirectToSuiteManager URL: ${suiteManagerUrl}`);
        const url = new URL(suiteManagerUrl, window.location.origin)
        const payload: Record<string, string> = { app: 'payments' }
        if (email) payload.email = email
        url.searchParams.set('data', encodeSuiteManagerData(payload))
        window.location.replace(url.toString())
    }
}
export const Auth0Provider = ({
                                  children,
                                  onRedirectCallback,
                                  ...initOptions
                              }: Auth0ProviderOptions & Auth0ClientOptions) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [auth0Disabled, setAuth0Disabled] = useState(false)
    const [isPopupOpen, setIsPopupOpen] = useState(false)
    const [user, setUser] = useState<Auth0User>()
    const [auth0Client, setAuth0Client] = useState<Auth0Client>()
    const [userProduct, setUserProduct] = useState<UserProductInterface>()

    useEffect(() => {
        
        const initSuiteManagerAuth = async () => {
            try {
                const userLogin = await getUserLogin();
                if (!!userLogin) {
                    clearStoredAuthLoginErrorMessage();
                    setIsAuthenticated(true);
                    setUserProduct(userLogin);
                    sessionStorage.setItem('suiteManagerRefresh', userLogin.refreshToken);
                } else {
                    // if user is not registered then we continue with the logout
                    redirectToSuiteManagerLogout();
                }
            } catch (e: any) {
                if (e.status === 401 || e.status === 403) {
                    const windowUrl = window.location.href;

                    // do not trigger logout if running mfe inside of Manager
                    const suiteManagerOrigin = /^https?:\/\/(suite\.frontsteps\.com|suite\.frontsteps-beta\.com|feature-\d+\.frontsteps-beta\.com)/;
                    if (suiteManagerOrigin.test(windowUrl)) {
                        return;
                    }

                    console.log('Unauthorized');
                    redirectToSuiteManagerLogout();
                } else if (e.status === 500) {
                    setAuthLoginErrorState(extractAuthLoginErrorMessage(e));
                }
            } finally {
                setLoading(false)
            }
        };
        const initAuth0 = async () => {
            _initOptions = initOptions
            const auth0FromHook = await getAuth0Client()
            setAuth0Client(auth0FromHook)

            if (window.location.search.includes('code=') &&
                window.location.search.includes("state=")) {
                let appState: RedirectLoginResult | undefined = {}
                try {
                    ({ appState } = await auth0FromHook.handleRedirectCallback())
                }
                finally {
                    if (appState)
                        onRedirectCallback(appState)
                }
            }

            const authed = await auth0FromHook.isAuthenticated()

            if (authed) {
                const userProfile: Auth0User | undefined = await auth0FromHook.getUser()
                setIsAuthenticated(true)
                setUser(userProfile)
            }

            setLoading(false)
        };
        
        const init = async () => {
            let isAuth0Disabled = false

            if (isAuthLoginErrorRoute() && hasStoredAuthLoginErrorMessage()) {
                setLoading(false)
                return
            }

            try {
                const authSettings = await getAuthSettings()
                isAuth0Disabled = !!authSettings?.auth0Disabled
                setAuth0Disabled(isAuth0Disabled)
            } catch (e) {
                setAuth0Disabled(false)
            }

            if (window.location.search.includes('accessToken=')) {
                const queryString = QueryString.parse(location.search);
                sessionStorage.setItem('suiteManagerToken', queryString?.accessToken?.toString() || '');
                if (queryString?.refreshToken) {
                    sessionStorage.setItem('suiteManagerRefresh', queryString.refreshToken.toString())
                }
                window.history.replaceState({}, document.title, window.location.pathname)
                await initSuiteManagerAuth();
                return
            }

            if (window.location.search.includes('data=')) {
                const queryString = QueryString.parse(location.search);
                const encodedData = queryString?.data?.toString()
                if (encodedData) {
                    try {
                        const suiteManagerData = decodeSuiteManagerData(encodedData)
                        if (suiteManagerData?.accessToken) {
                            sessionStorage.setItem('suiteManagerToken', suiteManagerData.accessToken.toString())
                        }
                        if (suiteManagerData?.refreshToken) {
                            sessionStorage.setItem('suiteManagerRefresh', suiteManagerData.refreshToken.toString())
                        }
                    } catch (e) {
                        console.error('Invalid Suite Manager data payload')
                    }
                }
                window.history.replaceState({}, document.title, window.location.pathname)
                await initSuiteManagerAuth();
                return
            }

            if (!!sessionStorage.getItem('suiteManagerToken')) {
                await initSuiteManagerAuth();
                return
            }

            await initAuth0()
        }

        init();
    }, [])

    const loginWithPopup = async (options?: PopupLoginOptions) => {
        setIsPopupOpen(true)

        try {
            await auth0Client!.loginWithPopup(options)
        } catch (error) {
            console.error(error)
        } finally {
            setIsPopupOpen(false)
        }

        const userProfile: Auth0User | undefined = await auth0Client!.getUser()
        setUser(userProfile)
        setIsAuthenticated(true)
    }

    const handleRedirectCallback = async () => {
        setLoading(true)

        const result = await auth0Client!.handleRedirectCallback()
        const userProfile:Auth0User | undefined = await auth0Client!.getUser()

        setLoading(false)
        setIsAuthenticated(true)
        setUser(userProfile)

        return result
    }

    const loginWithRedirect = async (options?: RedirectLoginOptions) => {
        return auth0Client!.loginWithRedirect(options)
    }

    const getTokenSilently = (options?: GetTokenSilentlyOptions) =>
    auth0Client!.getTokenSilently(options)

    const logout = (options?: LogoutOptions) => {
        if (hasSuiteManagerSession()) {
            redirectToSuiteManagerLogout(options?.returnTo);
            return
        }
        if(!!auth0Client){
            auth0Client!.logout(options);
        }
    }
    

    const getIdTokenClaims = async (options?: getIdTokenClaimsOptions) : Promise<IdToken> => {
        const token = await auth0Client!.getIdTokenClaims(options);
        if (token) {
            return token;
        }
        throw "Token invalid";
    }
    

    const getTokenWithPopup = (options?: GetTokenWithPopupOptions) =>
    auth0Client!.getTokenWithPopup(options)

    return (
        <Auth0Context.Provider
            value={{
                user,
                isAuthenticated,
                loading,
                auth0Disabled,
                isPopupOpen,
                userProduct,
                loginWithPopup,
                loginWithRedirect,
                logout,
                getTokenSilently,
                handleRedirectCallback,
                getIdTokenClaims,
                getTokenWithPopup
            }}
        >
            {children}
        </Auth0Context.Provider>
    );
};
